import * as sharedLinksActions from './actions';
import moment from 'moment';
import Notifications from 'react-notification-system-redux';
import { adopted } from '../../../general/apiScenarios';
import { pagePathChanged } from '../../../general/modules/router/actions';
import { logger } from '../../logger';
import { Page } from '../../models/page';
import { Messages } from './model';
import {
  getExpirationValue,
  getSelectedShareIds,
  getSorter,
  getSearchQuery,
  getPagination,
} from './selectors';
import { _select, ListenerApiLight, lm } from '../../listenerMiddleware';

export function sharedLinksListener() {
  lm.takeLatest(pagePathChanged, function (action, lapi) {
    const { page } = action.payload;
    if (page === Page.SHARED_LINKS) {
      lapi.dispatch(sharedLinksActions.loadSharesRequested());
    }
  });

  lm.takeLatest(
    sharedLinksActions.loadSharesRequested,
    async function (_, { getState, dispatch, signal }) {
      const sorter = getSorter(getState());
      const searchQuery = getSearchQuery(getState());
      const pagination = getPagination(getState());

      dispatch(sharedLinksActions.setInProgress({ inProgress: true }));
      const result = await adopted.sharedLinksAPI.loadShares({
        ordering: sorter ? `${sorter.order === 'desc' ? '-' : ''}${sorter.key}` : undefined,
        search: searchQuery.trim(),
        page: pagination.current,
      });
      if (signal.aborted) {
        return;
      }
      dispatch(sharedLinksActions.setInProgress({ inProgress: false }));

      dispatch(sharedLinksActions.selectorAllChanged({ shareIds: [] }));

      if (result.success) {
        dispatch(
          sharedLinksActions.sharesChanged({
            shares: result.data.response.sharing_list,
            total: result.data.response.count,
          }),
        );
      } else {
        if (result.data.status === 404 && pagination.current !== 1) {
          dispatch(sharedLinksActions.paginationChanged({ current: 1 }));
        } else {
          dispatch(Notifications.error(Messages.loadSharesError()));
          dispatch(sharedLinksActions.sharesChanged({ shares: [], total: 0 }));
        }
      }
    },
  );

  lm.takeLatest(sharedLinksActions.expirationButtonClicked, async function (_, lapi) {
    const selectedShareIDs: string[] = _select(lapi, getSelectedShareIds);
    if (selectedShareIDs.length !== 1) {
      return;
    }
    const shareID = selectedShareIDs[0];
    const isOk = await saveExpiration(lapi, shareID);
    lapi.signal.throwIfAborted();
    if (isOk) {
      lapi.dispatch(sharedLinksActions.loadSharesRequested());
    }
  });

  lm.takeEvery(
    [
      sharedLinksActions.sorterChanged,
      sharedLinksActions.searchQueryChanged,
      sharedLinksActions.paginationChanged,
    ],
    (_, { dispatch }) => {
      dispatch(sharedLinksActions.loadSharesRequested());
    },
  );
}

async function saveExpiration(lapi: ListenerApiLight, shareID: string) {
  lapi.dispatch(sharedLinksActions.saveExpirationStarted());
  const expirationValue: string = _select(lapi, getExpirationValue);

  try {
    const expiredAt = moment().add(expirationValue, 'day').toISOString();
    const result = await adopted.sharedLinksAPI.saveExpiration(shareID, expiredAt);
    if (lapi.signal.aborted) return;
    lapi.dispatch(sharedLinksActions.saveExpirationSuccess({ result }));
    logger.lapi(lapi, {
      action: 'Share',
      sub_action: 'Change Lifespan',
      extra: { text_2: shareID, text_1: expiredAt },
    });

    return true;
  } catch (error) {
    lapi.dispatch(sharedLinksActions.saveExpirationError({ error }));
    const text: string =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      error instanceof RangeError ? 'Invalid share duration' : (error as any).toString();
    lapi.dispatch(Notifications.error(Messages.saveExpirationError(text)));
    return false;
  }
}
